import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';

const DISMISSED_KEY = 'tilted_banner_dismissed';

export function getDeviceType() {
  const ua = navigator.userAgent || '';
  if (window.Capacitor?.isNativePlatform?.()) return 'native';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

// Android PWA Install Prompt — bottom bar
function AndroidPWABanner({ onDismiss }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (window.matchMedia('(display-mode: standalone)').matches) return null;
  if (!deferredPrompt) return null;

  const handleInstall = async () => {
    setInstalling(true);
    try {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        onDismiss();
      }
    } catch (err) {
      console.error('PWA install error:', err);
    } finally {
      setInstalling(false);
      setDeferredPrompt(null);
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-dive-dark border-t border-dive-gray p-4 animate-slide-up safe-bottom">
      <div className="max-w-md mx-auto flex items-center gap-4">
        <img src="/logo-icon.png" alt="Tilted" className="w-12 h-12 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-sm">Add Tilted to Home Screen</p>
          <p className="text-gray-400 text-xs">Quick access to dive bar ratings</p>
        </div>
        <button
          onClick={handleInstall}
          disabled={installing}
          className="flex items-center gap-1.5 px-4 py-2 bg-dive-amber text-white text-sm font-semibold rounded-lg flex-shrink-0"
        >
          <Download className="w-4 h-4" />
          {installing ? '...' : 'Install'}
        </button>
        <button onClick={onDismiss} className="text-gray-500 p-1 flex-shrink-0">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// iOS is handled by Apple's native Smart App Banner via <meta name="apple-itunes-app">
// in index.html. No custom React component needed for iOS.
export default function SmartAppBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return !!sessionStorage.getItem(DISMISSED_KEY); }
    catch { return false; }
  });

  const device = getDeviceType();

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISSED_KEY, '1'); } catch {}
  };

  if (dismissed || device !== 'android') return null;

  return <AndroidPWABanner onDismiss={handleDismiss} />;
}
